import { useState, useCallback, useMemo, useEffect } from 'react';
import { MdAdd, MdError, MdSearchOff } from 'react-icons/md';
import toast from 'react-hot-toast';
import api from '../../services/api';
import TrainingJournalCard from '../../components/TrainingJournal/TrainingJournalCard';
import CreatePostModal from '../../components/TrainingJournal/CreatePostModal';
import TrainingJournalFilters from '../../components/TrainingJournal/TrainingJournalFilters';
import { useDeleteTrainingPost } from '../../hooks/useTrainingJournal';
import useAuth from '../../hooks/useAuth';

function TrainingJournal() {
  const { user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    authorId: '',
    dateFrom: '',
    dateTo: '',
  });
  const [searchDebounce, setSearchDebounce] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [allPosts, setAllPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const { mutate: deletePost, isPending: isDeleting } = useDeleteTrainingPost();

  const POSTS_PER_PAGE = 10;

  // Debounced search
  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setIsSearching(true);

    if (searchDebounce) clearTimeout(searchDebounce);
    const timeout = setTimeout(() => {
      setIsSearching(false);
    }, 300);
    setSearchDebounce(timeout);
  };

  // Fetch posts on filter change
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoadingPosts(true);
        setPostsError(null);
        const params = {
          ...filters,
          page: 1,
          limit: POSTS_PER_PAGE,
        };
        const response = await api.get('/api/training-journal', { params });
        setAllPosts(response.data.posts);
        setTotalPosts(response.data.total);
        setCurrentPage(1);
      } catch (err) {
        setPostsError(err.response?.data?.message || 'Failed to load posts');
      } finally {
        setIsLoadingPosts(false);
      }
    };
    fetchPosts();
  }, [filters]);

  // Fetch posts when page changes
  useEffect(() => {
    if (currentPage > 1) {
      const fetchPosts = async () => {
        try {
          setIsLoadingPosts(true);
          const params = {
            ...filters,
            page: currentPage,
            limit: POSTS_PER_PAGE,
          };
          const response = await api.get('/api/training-journal', { params });
          setAllPosts((prev) => [...prev, ...response.data.posts]);
          setTotalPosts(response.data.total);
        } catch (err) {
          setPostsError(err.response?.data?.message || 'Failed to load posts');
        } finally {
          setIsLoadingPosts(false);
        }
      };
      fetchPosts();
    }
  }, [currentPage]);

  const handleToggleExpand = useCallback((postId) => {
    setExpandedPosts((prev) => ({ ...prev, [postId]: !prev[postId] }));
  }, []);

  const handleDelete = (postId) => {
    if (isDeleting) return;
    deletePost(postId, {
      onSuccess: () => {
        toast.success('Post deleted');
        setAllPosts((prev) => prev.filter((p) => p._id !== postId));
        setTotalPosts((prev) => prev - 1);
      },
      onError: (err) => toast.error(err.response?.data?.message || 'Failed to delete post'),
    });
  };

  const authors = useMemo(() => {
    const seen = new Set();
    const uniqueAuthors = [];
    allPosts.forEach((post) => {
      if (post.createdBy && !seen.has(post.createdBy._id)) {
        seen.add(post.createdBy._id);
        uniqueAuthors.push(post.createdBy);
      }
    });
    return uniqueAuthors;
  }, [allPosts]);

  const posts = allPosts;
  const hasMore = posts.length < totalPosts;
  const hasActiveFilters = [filters.search, filters.authorId, filters.dateFrom, filters.dateTo].filter(Boolean).length > 0;

  const canEditPost = (post) => post.createdBy._id === user._id;
  const canDeletePost = (post) => post.createdBy._id === user._id;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-secondary dark:text-gray-100">Training Journal</h1>
              <p className="text-xs text-muted dark:text-gray-400 mt-2">Daily training updates and club memories</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-3 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors text-sm"
            >
              <MdAdd className="text-base" /> New Post
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <TrainingJournalFilters
            filters={filters}
            onFilterChange={(f) => {
              if (f.search !== filters.search) {
                handleSearchChange(f.search);
              }
              setFilters(f);
            }}
            authors={authors}
            isSearching={isSearching}
          />
        </div>
      </div>

      {/* Result count */}
      {posts.length > 0 && (
        <div className="flex justify-center pt-4 px-4">
          <div className="w-full max-w-2xl">
            <p className="text-xs text-muted dark:text-gray-400">
              Showing {posts.length} of {totalPosts} posts
            </p>
          </div>
        </div>
      )}

      <div className="flex justify-center py-8 px-4 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="w-full max-w-2xl space-y-4">
        {postsError && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900/50 rounded-lg p-4 flex gap-3">
            <MdError className="text-red-700 dark:text-red-300 text-xl flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-900 dark:text-red-100">Failed to load posts. Please refresh.</p>
          </div>
        )}

        {currentPage === 1 && isLoadingPosts && (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg p-4 h-32 animate-pulse">
                <div className="h-full bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        )}

        {posts.length === 0 && !isLoadingPosts && (
          <div className="text-center py-16">
            <MdSearchOff className="text-5xl text-muted dark:text-gray-400 mx-auto mb-4" />
            <p className="text-muted dark:text-gray-400 text-lg mb-4">
              {hasActiveFilters ? 'No posts match your filters' : 'No training posts yet'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => setFilters({
                  search: '',
                  authorId: '',
                  dateFrom: '',
                  dateTo: '',
                })}
                className="text-primary hover:underline font-medium"
              >
                Clear filters
              </button>
            )}
            {!hasActiveFilters && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="text-primary hover:underline font-medium"
              >
                Create the first training update
              </button>
            )}
          </div>
        )}

        {posts.length > 0 && (
          <>
            <div className="flex flex-col space-y-6 sm:space-y-8 mb-6">
              {posts.map((post) => (
                <TrainingJournalCard
                  key={post._id}
                  post={post}
                  isExpanded={expandedPosts[post._id]}
                  onToggle={() => handleToggleExpand(post._id)}
                  onEdit={() => {}}
                  onDelete={handleDelete}
                  canEdit={canEditPost(post)}
                  canDelete={canDeletePost(post)}
                />
              ))}
            </div>

            <div className="flex flex-col items-center gap-4">
              {hasMore && (
                <button
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={isLoadingPosts}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-secondary dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                >
                  {isLoadingPosts ? 'Loading...' : 'Load More'}
                </button>
              )}

              {!hasMore && posts.length > 0 && (
                <p className="text-xs text-muted dark:text-gray-400">You've reached the end</p>
              )}
            </div>
          </>
        )}
        </div>
      </div>

      <CreatePostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => setFilters((p) => ({ ...p, page: 1 }))}
      />
    </div>
  );
}

export default TrainingJournal;
